import parse from 'html-react-parser'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import styles from './Index.module.scss'
import { Button, Card, Heading } from '@boltenergy-be/design-system'
import { routes } from 'types/routes'
import { AddAddress, TerminateAddress } from 'assets/svg/contracts'
import { isInactiveContract } from 'utils/contracts'
import ContractsLayout from 'features/contracts/layout/ContractsLayout.tsx'
import { ContractsIconAccentColors } from 'assets/svg/contracts/types.ts'
import { formatAddress } from 'utils/user.ts'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { setMoveFlow } from 'store/app/slice.ts'
import { ContractFlowStartPoints, ContractFlowTrackingTypes } from 'types/tracking.ts'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { IS_BETA } from 'constants/envs.ts'
import { AddressFormats } from 'types/format.ts'
import { trackEvent } from 'utils/tracking.ts'
import { MOVE_FLOWS, MoveFlow } from '@boltenergy-be/shared'
import BlockResubscriptionModal from 'components/block-resubscription-modal/BlockResubscriptionModal.tsx'
import { useCheckResubscriptionStatus } from 'hooks/useCheckResubscriptionStatus.ts'

const Index = () => {
  // Redux
  const { move } = useStoreSelector((store) => store.app)
  const dispatch = useStoreDispatch()
  const { billingContracts, selected } = useStoreSelector((store) => store.contact)

  // Hooks
  const resubscriptionStatus = useCheckResubscriptionStatus()

  // Contracts
  const contracts = billingContracts[selected.billingContract]
  const inactiveContract = contracts?.serviceContracts?.electricity ? isInactiveContract(contracts.serviceContracts.electricity) : false

  // i18n
  const { t } = useTranslation('contracts')

  // React router
  const navigate = useNavigate()

  // Local state
  const [showResubscriptionModal, setShowResubscriptionModal] = useState<boolean>(false)

  // Redirect to login if in beta (contracts flow inaccessible on beta)
  useEffect(() => {
    if (IS_BETA) {
      void navigate(routes.LOGIN)
    }
  }, [])

  /**
   * Tracks the contract flow from the contract overview with a custom flow
   * @param {string} flow
   * @param {(typeof ContractFlowTrackingTypes} type
   */
  const trackByFlow = (flow: string, type: ContractFlowTrackingTypes) => {
    trackEvent(
      { category: 'contract', object: `${type}_contract`, action: 'start' },
      { flow, type, from: ContractFlowStartPoints.CONTRACT_OVERVIEW }
    )
  }

  /**
   * Handles the continuation of a contract flow, tracking the event and navigating to the appropriate route based on the flow type
   * @param {MoveFlow} flow
   * @param {ContractFlowTrackingTypes} type
   */
  const handleContinue = (flow: MoveFlow, type = ContractFlowTrackingTypes.ADD) => {
    trackByFlow(flow, type)
    dispatch(setMoveFlow(flow))
    void navigate(type === ContractFlowTrackingTypes.TERMINATE ? routes.CONTRACTS_TERMINATE : routes.CONTRACTS_MOVE)
  }

  const handleStartMove = () => {
    if (resubscriptionStatus === 'block') {
      setShowResubscriptionModal(true)
    } else {
      handleContinue(MOVE_FLOWS.startStop)
    }
  }

  return (
    <>
      <ContractsLayout label={t(($) => $.header.labels.move)} flow={undefined}>
        <section className={styles.container}>
          <header className="grid-col-full">
            <Heading as="h2" variant="h4">
              {t(($) => $.index.title)}
            </Heading>
            <p>{t(($) => $.index.description)}</p>
          </header>

          <Card className={styles['action-card']}>
            <header>
              <AddAddress accent={ContractsIconAccentColors.PRIMARY} />
              <Heading as="h3" variant="h5">
                {t(($) => $.index.move.title)}
              </Heading>
            </header>
            <p>{t(($) => $.index.move.description)}</p>
            <Button isFullwidth onClick={handleStartMove} loading={resubscriptionStatus === 'checking'}>
              {t(($) => $.index.move.action)}
            </Button>
          </Card>

          {!inactiveContract && (
            <Card className={styles['action-card']}>
              <header>
                <TerminateAddress accent={ContractsIconAccentColors.PRIMARY} />
                <Heading as="h3" variant="h5">
                  {t(($) => $.index.terminate.title)}
                </Heading>
              </header>
              <p>{t(($) => $.index.terminate.description)}</p>

              {!!contracts?.serviceContracts.electricity?.deliveryPoint?.address && (
                <address className={styles.address}>
                  {parse(formatAddress(contracts.serviceContracts.electricity.deliveryPoint.address, AddressFormats.FULL_TWO_LINES) || '')}
                </address>
              )}

              <Button
                isFullwidth
                className={styles.link}
                loading={resubscriptionStatus === 'checking'}
                onClick={() => {
                  const flow = !move.started ? MOVE_FLOWS.stopStart : MOVE_FLOWS.startStop
                  handleContinue(flow, ContractFlowTrackingTypes.TERMINATE)
                }}
              >
                {t(($) => $.index.terminate.action)}
              </Button>
            </Card>
          )}

          <small className={classNames(styles.support, 'grid-col-full')}>{parse(t(($) => $.index.support))}</small>
        </section>
      </ContractsLayout>

      <BlockResubscriptionModal
        isOpen={showResubscriptionModal}
        onRequestClose={() => setShowResubscriptionModal(false)}
        onContinue={() => handleContinue(MOVE_FLOWS.startStop)}
      />
    </>
  )
}

export default Index
